import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import AdminLayout from "../../components/AdminLayout";
import { toast } from "sonner";
import axios from "axios";
import {
  ClipboardCheck, Eye, ThumbsUp, Trash2, FileText, ChevronUp,
  Filter, Search, AlertCircle, CheckCircle, Loader2, Star
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("token");

export default function PendingApprovals() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [actionId, setActionId] = useState(null);
  const [filter, setFilter] = useState("all"); // all | with-photo | no-photo
  const [search, setSearch] = useState("");
  const [bulkLoading, setBulkLoading] = useState(null); // {action, total}
  const [selected, setSelected] = useState(new Set()); // Set of article IDs
  const [featurePicker, setFeaturePicker] = useState(null); // article being featured

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === visible.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visible.map(a => a.id)));
    }
  };

  const clearSelection = () => setSelected(new Set());

  const authHeaders = { Authorization: `Bearer ${getToken()}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/articles/pending`, { headers: authHeaders });
      setArticles(res.data || []);
    } catch (err) {
      console.error("Load pending error", err);
      toast.error("Erro ao carregar matérias pendentes");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setActionId(id);
    try {
      await axios.post(`${API}/articles/${id}/approve`, {}, { headers: authHeaders });
      toast.success("Matéria publicada!");
      setArticles(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      console.error("Approve error", err);
      toast.error("Erro ao aprovar");
    } finally {
      setActionId(null);
    }
  };

  const reject = async (id) => {
    if (!window.confirm("Descartar esta matéria? Esta ação não pode ser desfeita.")) return;
    setActionId(id);
    try {
      await axios.post(`${API}/articles/${id}/reject`, {}, { headers: authHeaders });
      toast.success("Matéria descartada");
      setArticles(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      console.error("Reject error", err);
      toast.error("Erro ao descartar");
    } finally {
      setActionId(null);
    }
  };

  const approveAll = async () => {
    if (!window.confirm(`Aprovar TODAS as ${visible.length} matérias filtradas?`)) return;
    const ids = visible.map(a => a.id);
    setBulkLoading({ action: "approve", total: ids.length });
    try {
      const res = await axios.post(`${API}/articles/bulk-approve`, { ids }, {
        headers: authHeaders,
        timeout: 60000,
      });
      toast.success(`${res.data.approved} matérias publicadas!`);
      setArticles(prev => prev.filter(a => !ids.includes(a.id)));
    } catch (err) {
      console.error("Bulk approve error", err);
      toast.error("Erro ao aprovar em massa: " + (err.response?.data?.detail || err.message));
    } finally {
      setBulkLoading(null);
    }
  };

  const rejectAll = async () => {
    if (!window.confirm(`DESCARTAR TODAS as ${visible.length} matérias filtradas? Não pode ser desfeito.`)) return;
    const ids = visible.map(a => a.id);
    setBulkLoading({ action: "reject", total: ids.length });
    try {
      const res = await axios.post(`${API}/articles/bulk-reject`, { ids }, {
        headers: authHeaders,
        timeout: 60000,
      });
      toast.success(`${res.data.deleted} matérias descartadas!`);
      setArticles(prev => prev.filter(a => !ids.includes(a.id)));
    } catch (err) {
      console.error("Bulk reject error", err);
      toast.error("Erro ao descartar em massa: " + (err.response?.data?.detail || err.message));
    } finally {
      setBulkLoading(null);
    }
  };

  const approveSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Aprovar ${ids.length} matéria(s) selecionada(s)?`)) return;
    setBulkLoading({ action: "approve-sel", total: ids.length });
    try {
      const res = await axios.post(`${API}/articles/bulk-approve`, { ids }, { headers: authHeaders, timeout: 60000 });
      toast.success(`${res.data.approved} matérias publicadas!`);
      setArticles(prev => prev.filter(a => !ids.includes(a.id)));
      clearSelection();
    } catch (err) {
      console.error("Approve sel error", err);
      toast.error("Erro: " + (err.response?.data?.detail || err.message));
    } finally {
      setBulkLoading(null);
    }
  };

  const rejectSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Descartar ${ids.length} matéria(s) selecionada(s)? Não pode ser desfeito.`)) return;
    setBulkLoading({ action: "reject-sel", total: ids.length });
    try {
      const res = await axios.post(`${API}/articles/bulk-reject`, { ids }, { headers: authHeaders, timeout: 60000 });
      toast.success(`${res.data.deleted} matérias descartadas!`);
      setArticles(prev => prev.filter(a => !ids.includes(a.id)));
      clearSelection();
    } catch (err) {
      console.error("Reject sel error", err);
      toast.error("Erro: " + (err.response?.data?.detail || err.message));
    } finally {
      setBulkLoading(null);
    }
  };

  const setFeaturedWith = async (article, action, days = null) => {
    try {
      const body = { action };
      if (days) body.days = days;
      const res = await axios.post(`${API}/articles/${article.id}/feature`, body, { headers: authHeaders });
      const isFeatured = res.data?.is_featured;
      const featuredUntil = res.data?.featured_until;
      const featuredDays = res.data?.featured_days;
      if (isFeatured) {
        toast.success(featuredDays ? `Marcada como destaque por ${featuredDays} dias` : "Marcada como destaque (indefinido)");
      } else {
        toast.success("Removida dos destaques");
      }
      setArticles(prev => prev.map(a => a.id === article.id ? {
        ...a,
        is_featured: isFeatured,
        featured_until: featuredUntil,
        featured_days: featuredDays,
      } : a));
      setFeaturePicker(null);
    } catch (err) {
      console.error("Feature error", err);
      toast.error("Erro ao marcar destaque");
    }
  };

  const toggleFeatured = (article) => {
    if (article.is_featured) {
      setFeaturedWith(article, "unmark");
    } else {
      setFeaturePicker(article);
    }
  };

  const dedupeSimilar = async () => {
    if (!window.confirm("Remover matérias quase idênticas (mesmo artista + mesmo evento)? Mantém só a versão mais recente.")) return;
    setBulkLoading({ action: "dedupe", total: 0 });
    try {
      const res = await axios.post(`${API}/articles/dedupe-similar`, {}, {
        headers: authHeaders,
        timeout: 60000,
      });
      toast.success(`${res.data.deleted} duplicatas removidas (${res.data.groups_dedup} grupos)`);
      load();
    } catch (err) {
      console.error("Dedupe error", err);
      toast.error("Erro: " + (err.response?.data?.detail || err.message));
    } finally {
      setBulkLoading(null);
    }
  };

  const visible = articles.filter(a => {
    if (filter === "with-photo" && (!a.image_url || a.image_url.length < 4)) return false;
    if (filter === "no-photo" && a.image_url && a.image_url.length > 4) return false;
    if (search.trim() && !`${a.title} ${a.artist_name || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const counts = {
    total: articles.length,
    withPhoto: articles.filter(a => a.image_url && a.image_url.length > 4).length,
    noPhoto: articles.filter(a => !a.image_url || a.image_url.length < 4).length,
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-[#FF007F] flex items-center justify-center">
            <ClipboardCheck className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white" data-testid="approvals-title">
              Aprovações Pendentes
            </h1>
            <p className="text-zinc-500 text-sm">Revise e aprove ou descarte as matérias geradas pela automação</p>
          </div>
          <span className="px-4 py-2 bg-amber-500/15 border border-amber-500/30 rounded-xl text-amber-400 font-bold text-lg" data-testid="pending-count">
            {counts.total}
          </span>
        </div>

        {/* Stats / Filters */}
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => setFilter("all")}
            className={`p-4 rounded-xl border transition-all text-left ${
              filter === "all" ? "bg-[#FF007F]/15 border-[#FF007F]/50" : "bg-[#0F0F0F] border-white/10 hover:border-white/20"
            }`}
            data-testid="filter-all"
          >
            <p className="text-2xl font-bold text-white">{counts.total}</p>
            <p className="text-xs text-zinc-500">Todas</p>
          </button>
          <button
            onClick={() => setFilter("with-photo")}
            className={`p-4 rounded-xl border transition-all text-left ${
              filter === "with-photo" ? "bg-green-500/15 border-green-500/50" : "bg-[#0F0F0F] border-white/10 hover:border-white/20"
            }`}
            data-testid="filter-with-photo"
          >
            <p className="text-2xl font-bold text-green-400">{counts.withPhoto}</p>
            <p className="text-xs text-zinc-500">Com foto</p>
          </button>
          <button
            onClick={() => setFilter("no-photo")}
            className={`p-4 rounded-xl border transition-all text-left ${
              filter === "no-photo" ? "bg-red-500/15 border-red-500/50" : "bg-[#0F0F0F] border-white/10 hover:border-white/20"
            }`}
            data-testid="filter-no-photo"
          >
            <p className="text-2xl font-bold text-red-400">{counts.noPhoto}</p>
            <p className="text-xs text-zinc-500">Sem foto</p>
          </button>
        </div>

        {/* Search + Bulk actions */}
        <div className="flex gap-3 flex-wrap items-center">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por título ou artista..."
              className="w-full bg-[#0F0F0F] border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:border-[#FF007F] focus:outline-none"
              data-testid="search-input"
            />
          </div>
          {visible.length > 0 && (
            <>
              <button onClick={dedupeSimilar}
                disabled={bulkLoading !== null}
                className="px-4 py-2.5 bg-amber-600/20 hover:bg-amber-600 text-amber-400 hover:text-white text-sm font-bold rounded-xl border border-amber-600/30 flex items-center gap-2 transition-all disabled:opacity-50"
                data-testid="dedupe-similar-btn"
                title="Remove matérias quase iguais sobre o mesmo evento">
                {bulkLoading?.action === "dedupe" ? (
                  <><Loader2 size={16} className="animate-spin"/> Removendo...</>
                ) : (
                  <><Filter size={16}/> Remover Similares</>
                )}
              </button>
              <button onClick={approveAll}
                disabled={bulkLoading !== null}
                className="px-4 py-2.5 bg-green-600 hover:bg-green-500 text-white text-sm font-bold rounded-xl flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="approve-all">
                {bulkLoading?.action === "approve" ? (
                  <><Loader2 size={16} className="animate-spin"/> Aprovando {bulkLoading.total}...</>
                ) : (
                  <><ThumbsUp size={16}/> Aprovar Todas ({visible.length})</>
                )}
              </button>
              <button onClick={rejectAll}
                disabled={bulkLoading !== null}
                className="px-4 py-2.5 bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white text-sm font-bold rounded-xl border border-red-600/30 flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="reject-all">
                {bulkLoading?.action === "reject" ? (
                  <><Loader2 size={16} className="animate-spin"/> Descartando {bulkLoading.total}...</>
                ) : (
                  <><Trash2 size={16}/> Descartar Todas</>
                )}
              </button>
            </>
          )}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-4 border-[#FF007F] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : visible.length === 0 ? (
          <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-16 text-center">
            <CheckCircle className="w-16 h-16 text-green-500/50 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Tudo em dia! 🎉</h3>
            <p className="text-zinc-500">
              {articles.length === 0
                ? "Nenhuma matéria aguardando aprovação. A automação cria novas a cada 30 minutos."
                : "Nenhuma matéria corresponde aos filtros aplicados."}
            </p>
          </div>
        ) : (
          <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl divide-y divide-white/5 overflow-hidden">
            {/* Selection toolbar (sticky) */}
            <div className="bg-black/40 border-b border-white/10 px-4 py-2.5 flex items-center gap-3 sticky top-0 z-10">
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="select-all-toggle">
                <input
                  type="checkbox"
                  checked={selected.size > 0 && selected.size === visible.length}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 accent-[#FF007F] cursor-pointer"
                />
                <span className="text-xs text-zinc-400 font-medium">
                  {selected.size > 0 ? `${selected.size} selecionada(s)` : "Selecionar todas"}
                </span>
              </label>
              {selected.size > 0 && (
                <div className="flex items-center gap-2 ml-auto flex-wrap">
                  <button onClick={approveSelected} disabled={bulkLoading !== null}
                    className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="approve-selected">
                    {bulkLoading?.action === "approve-sel" ? (
                      <><Loader2 size={12} className="animate-spin"/> Aprovando...</>
                    ) : (
                      <><ThumbsUp size={12}/> Aprovar selecionadas</>
                    )}
                  </button>
                  <button onClick={rejectSelected} disabled={bulkLoading !== null}
                    className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white text-xs font-bold rounded-lg border border-red-600/30 flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="reject-selected">
                    {bulkLoading?.action === "reject-sel" ? (
                      <><Loader2 size={12} className="animate-spin"/> Descartando...</>
                    ) : (
                      <><Trash2 size={12}/> Descartar selecionadas</>
                    )}
                  </button>
                  <button onClick={clearSelection}
                    className="text-xs text-zinc-500 hover:text-white px-2 py-1.5">
                    Limpar
                  </button>
                </div>
              )}
            </div>
            {visible.map(article => (
              <div key={article.id} className={`p-4 hover:bg-white/[0.02] transition-colors ${selected.has(article.id) ? "bg-[#FF007F]/5" : ""}`} data-testid={`pending-${article.id}`}>
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={selected.has(article.id)}
                    onChange={() => toggleSelect(article.id)}
                    className="w-4 h-4 accent-[#FF007F] cursor-pointer mt-2 flex-shrink-0"
                    data-testid={`select-${article.id}`}
                  />
                  {/* Thumbnail */}
                  <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-lg overflow-hidden bg-black/40 flex-shrink-0 flex items-center justify-center">
                    {article.image_url ? (
                      <img src={article.image_url} alt="" className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = "none"; }} />
                    ) : (
                      <AlertCircle className="text-red-400/60" size={32} />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className="bg-amber-500/20 text-amber-400 text-xs font-bold px-2 py-0.5 rounded">RASCUNHO</span>
                      <span className="bg-white/10 text-zinc-400 text-xs px-2 py-0.5 rounded">{article.category}</span>
                      {article.image_origin === "source" && (
                        <span className="bg-green-500/15 text-green-400 text-xs px-2 py-0.5 rounded font-semibold">📷 Foto da fonte</span>
                      )}
                      {article.image_origin === "bank" && (
                        <span className="bg-blue-500/15 text-blue-400 text-xs px-2 py-0.5 rounded font-semibold">📷 Banco interno</span>
                      )}
                      {(!article.image_url || article.image_origin === "none") && (
                        <span className="bg-red-500/15 text-red-400 text-xs px-2 py-0.5 rounded font-semibold">⚠️ Sem foto</span>
                      )}
                      {article.source_name && (
                        <span className="text-zinc-600 text-xs">via {article.source_name}</span>
                      )}
                    </div>

                    <h3 className="text-white font-semibold leading-snug mb-1">{article.title}</h3>
                    <p className="text-zinc-500 text-sm line-clamp-2">{article.excerpt}</p>

                    {expandedId === article.id && (
                      <div className="mt-3 p-4 bg-black/40 rounded-lg border border-white/5">
                        <div
                          className="text-zinc-300 text-sm leading-relaxed prose prose-invert max-w-none"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content || "") }}
                        />
                        {article.artist_name && (
                          <p className="text-zinc-500 text-xs mt-3">Artista detectado: {article.artist_name}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => setExpandedId(expandedId === article.id ? null : article.id)}
                      className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400 transition-all"
                      title={expandedId === article.id ? "Ocultar" : "Visualizar"}
                      data-testid={`toggle-${article.id}`}
                    >
                      {expandedId === article.id ? <ChevronUp size={16}/> : <Eye size={16}/>}
                    </button>
                    <button
                      onClick={() => navigate(`/admin/articles/edit/${article.id}`)}
                      className="p-2 rounded-lg bg-[#00E5FF]/10 hover:bg-[#00E5FF]/20 text-[#00E5FF] transition-all"
                      title="Editar"
                      data-testid={`edit-${article.id}`}
                    >
                      <FileText size={16}/>
                    </button>
                    <button
                      onClick={() => toggleFeatured(article)}
                      className={`p-2 rounded-lg transition-all ${
                        article.is_featured
                          ? "bg-amber-500 text-white"
                          : "bg-amber-500/10 hover:bg-amber-500/20 text-amber-400"
                      }`}
                      title={
                        article.is_featured
                          ? (article.featured_until
                              ? `Em destaque até ${new Date(article.featured_until).toLocaleDateString("pt-BR")} — clique para remover`
                              : "Em destaque (indefinido) — clique para remover")
                          : "Marcar como destaque"
                      }
                      data-testid={`feature-${article.id}`}
                    >
                      <Star size={16} fill={article.is_featured ? "currentColor" : "none"}/>
                    </button>
                    <button
                      onClick={() => approve(article.id)}
                      disabled={actionId === article.id}
                      className="p-2 rounded-lg bg-green-600/20 hover:bg-green-600 text-green-400 hover:text-white transition-all disabled:opacity-50"
                      title="Aprovar e publicar"
                      data-testid={`approve-${article.id}`}
                    >
                      <ThumbsUp size={16}/>
                    </button>
                    <button
                      onClick={() => reject(article.id)}
                      disabled={actionId === article.id}
                      className="p-2 rounded-lg bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white transition-all disabled:opacity-50"
                      title="Descartar"
                      data-testid={`reject-${article.id}`}
                    >
                      <Trash2 size={16}/>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Feature duration picker */}
      {featurePicker && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setFeaturePicker(null)}
          data-testid="feature-picker-overlay"
        >
          <div
            className="bg-[#121212] border border-white/10 rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <Star size={20} className="text-amber-400" fill="currentColor" />
              <h3 className="text-lg font-bold text-white">Marcar como destaque</h3>
            </div>
            <p className="text-zinc-400 text-sm mb-1">Por quanto tempo essa matéria deve ficar em destaque na home?</p>
            <p className="text-zinc-500 text-xs mb-4">Apenas o admin pode remover o destaque depois.</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { label: "7 dias", days: 7 },
                { label: "14 dias", days: 14 },
                { label: "30 dias", days: 30 },
                { label: "Indefinido", days: null },
              ].map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => setFeaturedWith(featurePicker, "mark", opt.days)}
                  data-testid={`feature-duration-${opt.days || "indefinite"}`}
                  className="px-4 py-3 bg-amber-500/15 hover:bg-amber-500/30 text-amber-300 hover:text-amber-200 font-bold rounded-lg border border-amber-500/30 transition-all"
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setFeaturePicker(null)}
              data-testid="feature-picker-cancel"
              className="w-full px-4 py-2 bg-transparent border border-white/10 text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
